import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
  Image,
  TextInput,
  Modal,
  Pressable,
  ActivityIndicator,
} from 'react-native';
import { useRoute, useNavigation, RouteProp, useFocusEffect } from '@react-navigation/native';
import { formatPct } from '../../utils/formatPercentage';
import { commitmentColor } from '../../utils/metricColors';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path, Circle } from 'react-native-svg';
import { goalsApi, goalCommentsApi, GoalComment, servicesApi, processesApi, unitGoalsApi } from '../../services/apiService';
import { ServiceList, ProcessList } from '../../types';
import { StrategicGoal, KPIList } from '../../types';
import DeletionImpactModal from '../../components/DeletionImpactModal';
import DeletionApprovalBanner from '../../components/DeletionApprovalBanner';
import { COLORS, FONTS } from '../../constants/config';
import { LoadingScreen, EmptyState, PageContainer, Navbar, Breadcrumb, EvidenceTable } from '../../components';
import TargetAchievementCard from '../../components/TargetAchievementCard';
import { ChevronLeftIcon, PlusIcon, EyeIcon, EllipsisVerticalIcon, CheckIcon, XIcon, EditIcon, TrashIcon, ArrowRightIcon } from '../../components/Icons';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useResponsive, useRoleNavigation, useAutoTranslate } from '../../hooks';
import { isPercentUnit } from '../../utils/targetGrid';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { useYear } from '../../contexts/YearContext';
import { useCachedData } from '../../hooks/useCachedData';

import { tokens } from '@design';
import { IconButton } from '@design/components/IconButton';
import { PageHeader } from '@design/components/PageHeader';
type RouteProps = RouteProp<MainStackParamList, 'GoalDetail'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// SVG Icon Components (remaining icons not in shared Icons.tsx)
// ============================================================

const MessageCirclePlusIcon = ({ size = 20, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M7.9 20A9 9 0 1 0 4 16.1L2 22l5.9-2z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 12h8M12 8v8" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ClockIcon = ({ size = 14, color = '#9467BD' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Circle cx="12" cy="12" r="10" stroke={color} strokeWidth="2"/>
    <Path d="M12 6V12L16 14" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const LockIcon = ({ size = 20, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M19 11H5C3.89543 11 3 11.8954 3 13V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V13C21 11.8954 20.1046 11 19 11Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const SendIcon = ({ size = 16, color = tokens.colors.raw.white }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M22 2L11 13" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M22 2L15 22L11 13L2 9L22 2Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const WeightIcon = ({ size = 20, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 8C13.6569 8 15 6.65685 15 5C15 3.34315 13.6569 2 12 2C10.3431 2 9 3.34315 9 5C9 6.65685 10.3431 8 12 8Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M6.49973 8C6.06698 8.00449 5.64737 8.14923 5.30388 8.41248C4.96039 8.67573 4.71155 9.0433 4.59473 9.46L2.09973 18.5C2.02413 18.7926 2.01582 19.0985 2.07541 19.3947C2.135 19.691 2.26096 19.9699 2.44381 20.2105C2.62666 20.4511 2.86165 20.6471 3.13114 20.7839C3.40062 20.9206 3.69759 20.9945 3.99973 21H19.9997C20.3084 20.9999 20.6129 20.9283 20.8893 20.7909C21.1657 20.6535 21.4065 20.454 21.593 20.208C21.7794 19.962 21.9064 19.6762 21.9639 19.3729C22.0215 19.0697 22.0081 18.7572 21.9247 18.46L19.3997 9.5C19.2896 9.07341 19.0417 8.69512 18.6945 8.42388C18.3473 8.15265 17.9203 8.00364 17.4797 8H6.49973Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const SquarePenIcon = ({ size = 20, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 3H5C4.46957 3 3.96086 3.21071 3.58579 3.58579C3.21071 3.96086 3 4.46957 3 5V19C3 19.5304 3.21071 20.0391 3.58579 20.4142C3.96086 20.7893 4.46957 21 5 21H19C19.5304 21 20.0391 20.7893 20.4142 20.4142C20.7893 20.0391 21 19.5304 21 19V12" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M18.3751 2.62523C18.7729 2.2274 19.3125 2.00391 19.8751 2.00391C20.4377 2.00391 20.9773 2.2274 21.3751 2.62523C21.7729 3.02305 21.9964 3.56262 21.9964 4.12523C21.9964 4.68784 21.7729 5.2274 21.3751 5.62523L12.3621 14.6392C12.1246 14.8765 11.8313 15.0501 11.5091 15.1442L8.63609 15.9842C8.55005 16.0093 8.45883 16.0108 8.372 15.9886C8.28517 15.9663 8.20592 15.9212 8.14254 15.8578C8.07916 15.7944 8.03398 15.7151 8.01174 15.6283C7.98949 15.5415 7.991 15.4503 8.01609 15.3642L8.85609 12.4912C8.95062 12.1693 9.12463 11.8763 9.36209 11.6392L18.3751 2.62523Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// Circular Gauge Component
// ============================================================

interface CircularGaugeProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
  backgroundColor?: string;
  showDot?: boolean;
}

const CircularGauge = ({
  percentage,
  size = 120,
  strokeWidth = 10,
  color = tokens.colors.raw.green["500"],
  backgroundColor = tokens.colors.raw.gray["200"],
  showDot = true,
}: CircularGaugeProps) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const center = size / 2;

  // Calculate dot position at END of progress arc
  // -90 offset to start from top (12 o'clock position)
  const angle = ((percentage / 100) * 360 - 90) * (Math.PI / 180);
  const dotX = center + radius * Math.cos(angle);
  const dotY = center + radius * Math.sin(angle);

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size}>
        {/* Background circle */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={backgroundColor}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress arc - rotation applied to this element only */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={color}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
        {/* Dot at end of progress - ONLY show if percentage > 0 */}
        {showDot && percentage > 0 && (
          <Circle
            cx={dotX}
            cy={dotY}
            r={strokeWidth / 2 + 2}
            fill={color}
            stroke="#FCFCFC"
            strokeWidth={2}
          />
        )}
      </Svg>
      <View style={{ position: 'absolute', alignItems: 'center' }}>
        <Text style={{ fontSize: tokens.typography["heading 4"]["font size"], fontFamily: FONTS.numbers, fontWeight: '500', color: tokens.colors.semantic.general.foreground }}>
          %{formatPct(percentage)}
        </Text>
      </View>
    </View>
  );
};

// ============================================================
// Donut Chart Component (for status distribution)
// ============================================================

interface DonutChartProps {
  data: { value: number; color: string; label: string }[];
  size?: number;
  strokeWidth?: number;
}

const DonutChart = ({ data, size = 100, strokeWidth = 20 }: DonutChartProps) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const center = size / 2;

  let currentOffset = 0;
  const total = data.reduce((sum, item) => sum + item.value, 0);

  return (
    <View style={{ width: size, height: size }}>
      <Svg width={size} height={size} style={{ transform: [{ rotate: '-90deg' }] }}>
        {/* Background circle */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={tokens.colors.raw.gray["200"]}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Data segments */}
        {data.map((item, index) => {
          const segmentLength = (item.value / total) * circumference;
          const offset = currentOffset;
          currentOffset += segmentLength;

          if (item.value === 0) return null;

          return (
            <Circle
              key={index}
              cx={center}
              cy={center}
              r={radius}
              stroke={item.color}
              strokeWidth={strokeWidth}
              fill="none"
              strokeDasharray={`${segmentLength} ${circumference - segmentLength}`}
              strokeDashoffset={-offset}
              strokeLinecap="round"
            />
          );
        })}
      </Svg>
    </View>
  );
};

// ============================================================
// Linkage Card Component (for sections with department badge)
// Matches Figma design for المبادرات, الخدمات, العمليات, أهداف الوحدات
// ============================================================

interface LinkageCardProps {
  title: string;
  department?: string;
  /** Commitment rate; null = nothing due yet (renders as — with a muted bar, not a red 0%) */
  percentage: number | null;
  status: 'on_track' | 'at_risk' | 'behind';
  onPress?: () => void;
  cardWidth?: number;
}

const LinkageCard = ({ title, department, percentage, status, onPress, cardWidth }: LinkageCardProps) => {
  const { t } = useLanguage();
  const noData = percentage == null;
  // Green (#2CA02C) for on_track, Orange (#FF7F0E) for at_risk, Red for behind
  const statusColor = noData
    ? tokens.colors.raw.gray["400"]
    : status === 'on_track' ? '#2CA02C' : status === 'at_risk' ? '#FF7F0E' : '#D62728';

  return (
    <View style={[styles.linkageCard, cardWidth ? { width: cardWidth } : undefined]}>
      {/* Title */}
      <View style={styles.linkageCardTitleContainer}>
        <Text style={styles.linkageCardTitle} numberOfLines={2}>{title}</Text>
      </View>

      {/* Department badge (if provided) */}
      {department && (
        <View style={styles.linkageCardDepartmentContainer}>
          <View style={styles.linkageCardDepartmentBadge}>
            <Text style={styles.linkageCardDepartmentText} numberOfLines={1}>{department}</Text>
          </View>
        </View>
      )}

      {/* Progress row with percentage badge and progress bar */}
      <View style={styles.linkageCardProgressRow}>
        <View style={styles.linkageCardValueBadge}>
          <Text style={styles.linkageCardValueText}>{noData ? '—' : `% ${percentage}`}</Text>
        </View>
        <View style={styles.linkageCardProgressContainer}>
          <View style={styles.linkageCardProgressBg}>
            <View
              style={[
                styles.linkageCardProgressFill,
                {
                  width: `${Math.min(percentage ?? 0, 100)}%`,
                  backgroundColor: statusColor,
                },
              ]}
            >
              <View style={[styles.linkageCardProgressDot, { backgroundColor: statusColor }]} />
            </View>
          </View>
        </View>
      </View>

      {/* Details link */}
      <TouchableOpacity style={styles.linkageCardDetailsLink} onPress={onPress}>
        <ChevronLeftIcon size={14} color={tokens.colors.semantic.general.foreground} />
        <Text style={styles.linkageCardDetailsText}>{t('common.viewDetails')}</Text>
      </TouchableOpacity>
    </View>
  );
};

// ============================================================
// Comment Card Component (matches Figma node 2462:154109)
// ============================================================

interface CommentData {
  id: number;
  name: string;
  initials: string;
  date: string;
  text: string;
  avatarColor: string;
  showMenu: boolean;
  isEdited?: boolean;
  originalText?: string;
  userType?: 'strategy_team' | 'org_employee' | 'superuser' | 'executive';
}

interface CommentProps {
  comment: CommentData;
  onEdit?: (comment: CommentData) => void;
  onDelete?: (id: number) => void;
}

const CommentCard = ({ comment, onEdit, onDelete }: CommentProps) => {
  const { t } = useLanguage();
  const [menuVisible, setMenuVisible] = useState(false);
  const [showOriginalPopup, setShowOriginalPopup] = useState(false);

  const handleEdit = () => {
    setMenuVisible(false);
    onEdit?.(comment);
  };

  const handleDelete = () => {
    setMenuVisible(false);
    onDelete?.(comment.id);
  };

  // Get user type label and badge color
  const getUserTypeBadge = () => {
    switch (comment.userType) {
      case 'strategy_team':
        return { label: t('userTypes.strategyTeam'), color: '#0F9CD8', bgColor: tokens.colors.raw.amber["100"] };
      case 'org_employee':
        return { label: t('userTypes.orgEmployee'), color: '#9467BD', bgColor: '#F3E8FF' };
      case 'superuser':
        return { label: t('userTypes.superuser'), color: '#2563EB', bgColor: '#DBEAFE' };
      case 'executive':
        return { label: t('userTypes.executive'), color: '#059669', bgColor: '#D1FAE5' };
      default:
        return null;
    }
  };

  const userTypeBadge = getUserTypeBadge();

  return (
    <View style={[styles.commentCard, menuVisible && styles.commentCardMenuOpen]}>
      {/* Header row with avatar, name/date, and optional menu */}
      <View style={styles.commentHeaderRow}>
        {/* Menu icon (optional) */}
        {comment.showMenu && (
          <View style={styles.commentMenuWrapper}>
            <TouchableOpacity
              style={styles.commentMenuButton}
              onPress={() => setMenuVisible(!menuVisible)}
            >
              <EllipsisVerticalIcon size={16} color={tokens.colors.semantic.general.foreground} />
            </TouchableOpacity>

            {/* Dropdown Menu */}
            {menuVisible && (
              <View style={styles.commentMenuDropdown}>
                <TouchableOpacity style={styles.commentMenuItem} onPress={handleEdit}>
                  <Text style={styles.commentMenuItemText}>{t('common.edit')}</Text>
                  <EditIcon size={16} color={tokens.colors.semantic.general.foreground} />
                </TouchableOpacity>
                <View style={styles.commentMenuSeparator} />
                <TouchableOpacity style={styles.commentMenuItem} onPress={handleDelete}>
                  <Text style={styles.commentMenuItemTextDelete}>{t('common.delete')}</Text>
                  <TrashIcon size={16} color={tokens.colors.raw.red["500"]} />
                </TouchableOpacity>
              </View>
            )}
          </View>
        )}

        {/* Name and date */}
        <View style={styles.commentNameDateContainer}>
          {/* Name on top */}
          <Text style={styles.commentName}>{comment.name}</Text>
          {/* User type badge and edited tag below name */}
          <View style={styles.commentBadgeRow}>
            {/* Edited tag */}
            {comment.isEdited && (
              <TouchableOpacity
                style={styles.editedTag}
                onPress={() => setShowOriginalPopup(true)}
              >
                <Text style={styles.editedTagText}>{t('comments.edited')}</Text>
              </TouchableOpacity>
            )}
            {/* User type badge */}
            {userTypeBadge && (
              <View style={[styles.userTypeBadge, { backgroundColor: userTypeBadge.bgColor }]}>
                <Text style={[styles.userTypeBadgeText, { color: userTypeBadge.color }]}>
                  {userTypeBadge.label}
                </Text>
              </View>
            )}
          </View>
          <Text style={styles.commentDate}>{comment.date}</Text>
        </View>

        {/* Avatar */}
        <View style={[styles.commentAvatar, { backgroundColor: comment.avatarColor }]}>
          <Text style={styles.commentAvatarText}>{comment.initials}</Text>
        </View>
      </View>

      {/* Separator line */}
      <View style={styles.commentSeparator} />

      {/* Comment text */}
      <View style={styles.commentTextContainer}>
        <Text style={styles.commentText}>{comment.text}</Text>
      </View>

      {/* Original Text Popup Modal */}
      <Modal
        visible={showOriginalPopup}
        transparent
        animationType="fade"
        onRequestClose={() => setShowOriginalPopup(false)}
      >
        <Pressable
          style={styles.originalTextModalOverlay}
          onPress={() => setShowOriginalPopup(false)}
        >
          <View style={styles.originalTextModalContent}>
            <View style={styles.originalTextModalHeader}>
              <TouchableOpacity onPress={() => setShowOriginalPopup(false)}>
                <XIcon size={18} color={tokens.colors.semantic.general.foreground} />
              </TouchableOpacity>
              <Text style={styles.originalTextModalTitle}>{t('comments.originalComment')}</Text>
            </View>
            <View style={styles.originalTextModalBody}>
              <Text style={styles.originalTextModalText}>
                {comment.originalText || t('comments.noOriginalText')}
              </Text>
            </View>
          </View>
        </Pressable>
      </Modal>
    </View>
  );
};

// ============================================================
// Add Comment Input Component (matches Figma node 2569:211200)
// ============================================================

interface AddCommentInputProps {
  onSubmit: (text: string) => void;
  onCancel: () => void;
  editingComment?: CommentData | null;
}

const AddCommentInput = ({ onSubmit, onCancel, editingComment }: AddCommentInputProps) => {
  const { t } = useLanguage();
  const [text, setText] = useState('');
  const { colors, isDark } = useTheme();

  const commentCardTheme = {
    background: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["100"],
    text: colors.textPrimary,
    inputBg: isDark ? colors.card : tokens.colors.raw.white,
    inputText: colors.textPrimary,
    placeholder: colors.textMuted,
  };

  // Initialize text when editingComment changes
  useEffect(() => {
    if (editingComment) {
      setText(editingComment.text);
    } else {
      setText('');
    }
  }, [editingComment]);

  const handleSubmit = () => {
    if (text.trim()) {
      onSubmit(text.trim());
      setText('');
    }
  };

  const handleCancel = () => {
    setText('');
    onCancel();
  };

  return (
    <View style={[styles.addCommentCard, { backgroundColor: commentCardTheme.background }]}>
      {/* Header */}
      <View style={styles.addCommentHeader}>
        <Text style={[styles.addCommentHeaderText, { color: commentCardTheme.text }]}>
          {editingComment ? t('comments.editComment') : t('comments.addComment')}
        </Text>
      </View>

      {/* Separator */}
      <View style={styles.addCommentSeparator} />

      {/* Input area */}
      <View style={[styles.addCommentInputContainer, { backgroundColor: commentCardTheme.inputBg }]}>
        <TextInput
          style={[styles.addCommentInput, { color: commentCardTheme.inputText }]}
          value={text}
          onChangeText={setText}
          placeholder={t('comments.writeCommentPlaceholder')}
          placeholderTextColor={commentCardTheme.placeholder}
          multiline
          textAlign="right"
        />
      </View>

      {/* Separator */}
      <View style={styles.addCommentSeparator} />

      {/* Action buttons */}
      <View style={styles.addCommentActions}>
        <TouchableOpacity style={styles.addCommentActionButton} onPress={handleCancel}>
          <XIcon size={16} color={commentCardTheme.text} />
        </TouchableOpacity>
        <TouchableOpacity style={styles.addCommentActionButton} onPress={handleSubmit}>
          <CheckIcon size={16} color={commentCardTheme.text} />
        </TouchableOpacity>
      </View>
    </View>
  );
};

// ============================================================
// Main Component
// ============================================================

export default function GoalDetailScreen() {
  const route = useRoute<RouteProps>();
  const navigation = useNavigation<NavigationProp>();
  const { id, fromProcess, organizationId, organizationName, orgNavSource } = route.params;
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();
  const { navigateToHome, isViewOnly, canComment } = useRoleNavigation();
  const { isMobile, isTablet, isDesktop, isSmallMobile, horizontalCardWidth, containerPadding, cardPadding, sectionSpacing } = useResponsive();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const { translate, translateOrg, translateStat } = useAutoTranslate();
  const { selectedYear } = useYear();

  // Theme colors object
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textSecondary: colors.textSecondary,
    textMuted: colors.textMuted,
    iconDefault: colors.iconDefault,
    border: colors.border,
    backgroundAlt: colors.backgroundAlt,
  };

  // Check if user is strategy_team (they can't add KPIs, initiatives, activities)
  const isStrategyTeam = user?.profile?.user_type === 'strategy_team' || user?.profile?.user_type === 'superuser';

  // Stack on tablet + mobile (< 1024px) — tablet too narrow for desktop grid.
  const isSmallScreen = !isDesktop;

  const { data: goal, setData: setGoal, loading, setLoading } = useCachedData<StrategicGoal>(`goal-${id}`);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeletionImpact, setShowDeletionImpact] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const isOrgEmployee = user?.profile?.user_type === 'org_employee';
  const canApproveDeletion = ['org_executive', 'strategy_team', 'superuser'].includes(user?.profile?.user_type || '');
  const canManageEvidence = ['org_employee', 'coordinator', 'superuser'].includes(user?.profile?.user_type || '');
  const canViewAllEvidence = ['strategy_team', 'executive', 'performance_evaluator', 'evaluation_approver', 'superuser'].includes(user?.profile?.user_type || '');

  // KPIs state - fetched from API
  const [strategicKPIs, setStrategicKPIs] = useState<KPIList[]>([]);
  const [kpisLoading, setKpisLoading] = useState(false);

  // Initiatives state - fetched from API
  const [initiatives, setInitiatives] = useState<any[]>([]);
  const [initiativesLoading, setInitiativesLoading] = useState(false);

  // Services state - fetched from API
  const [services, setServices] = useState<ServiceList[]>([]);
  const [servicesLoading, setServicesLoading] = useState(false);

  // Processes state - fetched from API
  const [processes, setProcesses] = useState<ProcessList[]>([]);
  const [processesLoading, setProcessesLoading] = useState(false);

  // Unit Goals state - fetched from API
  const [unitGoals, setUnitGoals] = useState<any[]>([]);
  const [unitGoalsLoading, setUnitGoalsLoading] = useState(false);

  // Comments state management - fetched from API
  const [apiComments, setApiComments] = useState<GoalComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [showAddComment, setShowAddComment] = useState(false);
  const [editingComment, setEditingComment] = useState<CommentData | null>(null);

  // Convert API comment to CommentData format for display
  const convertApiComment = (apiComment: GoalComment): CommentData => {
    // Get the actual author name from author_details
    const fullName = apiComment.author_details?.full_name || apiComment.author_details?.username || t('common.user');
    const nameParts = fullName.split(' ').filter(Boolean);
    const authorName = nameParts[0] || t('common.user'); // First name only

    // Generate initials from the actual name
    const initials = nameParts.length >= 2
      ? `${nameParts[0][0]} ${nameParts[1][0]}`
      : fullName.slice(0, 2);

    const isOwner = user?.id === apiComment.author;

    // Colors based on author type
    const colorMap: Record<string, string> = {
      'strategy_team': '#0F9CD8',
      'org_employee': '#9467BD',
    };
    const avatarColor = colorMap[apiComment.author_type] || tokens.colors.raw.blue["500"];

    return {
      id: apiComment.id,
      name: authorName,
      initials,
      date: new Date(apiComment.created_at).toLocaleDateString('ar-SA', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
      text: apiComment.content,
      avatarColor,
      showMenu: isOwner, // Only show menu for own comments
      isEdited: apiComment.is_edited,
      originalText: apiComment.original_content,
      userType: apiComment.author_type as 'strategy_team' | 'org_employee' | 'superuser' | 'executive',
    };
  };

  // Fetch KPIs from API
  const fetchKPIs = useCallback(async () => {
    try {
      setKpisLoading(true);
      const data = await goalsApi.getKPIs(id);
      setStrategicKPIs(data);
    } catch (err: any) {
      console.error('Error fetching KPIs:', err);
    } finally {
      setKpisLoading(false);
    }
  }, [id]);

  // Fetch Initiatives from API
  const fetchInitiatives = useCallback(async () => {
    try {
      setInitiativesLoading(true);
      const data = await goalsApi.getInitiatives(id);
      setInitiatives(data);
    } catch (err: any) {
      console.error('Error fetching initiatives:', err);
    } finally {
      setInitiativesLoading(false);
    }
  }, [id]);

  // Fetch Services from API
  const fetchServices = useCallback(async () => {
    try {
      setServicesLoading(true);
      const data = await servicesApi.list({ strategic_goal: id });
      setServices(data.results);
    } catch (err: any) {
      console.error('Error fetching services:', err);
    } finally {
      setServicesLoading(false);
    }
  }, [id]);

  // Fetch Processes from API - filter by strategic goal
  const fetchProcesses = useCallback(async () => {
    try {
      setProcessesLoading(true);
      const data = await processesApi.list({ strategic_goals: id, page_size: 100 });
      setProcesses(data.results);
    } catch (err: any) {
      console.error('Error fetching processes:', err);
    } finally {
      setProcessesLoading(false);
    }
  }, [id]);

  // Fetch Unit Goals from API - filter by strategic goal
  const fetchUnitGoals = useCallback(async () => {
    try {
      setUnitGoalsLoading(true);
      const data = await unitGoalsApi.list({ strategic_goal: id, page_size: 100 });
      setUnitGoals(data.results);
    } catch (err: any) {
      console.error('Error fetching unit goals:', err);
    } finally {
      setUnitGoalsLoading(false);
    }
  }, [id]);

  // Fetch comments from API
  const fetchComments = useCallback(async () => {
    try {
      setCommentsLoading(true);
      const data = await goalCommentsApi.getForGoal(id);
      setApiComments(data);
    } catch (err: any) {
      console.error('Error fetching comments:', err);
    } finally {
      setCommentsLoading(false);
    }
  }, [id]);

  // Load initiatives, services, processes, unit goals, KPIs and comments when goal ID changes
  useEffect(() => {
    fetchKPIs();
    fetchInitiatives();
    fetchServices();
    fetchProcesses();
    fetchUnitGoals();
    fetchComments();
    setShowAddComment(false);
    setEditingComment(null);
  }, [id, fetchKPIs, fetchInitiatives, fetchServices, fetchProcesses, fetchUnitGoals, fetchComments]);

  // Convert API comments to display format
  const comments = apiComments.map(convertApiComment);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const data = await goalsApi.get(id, { year: selectedYear });
      setGoal(data);
    } catch (err: any) {
      setError(err.message || t('goals.loadFailed'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [id, selectedYear]);

  // Auto-refresh when screen comes into focus
  useFocusEffect(
    useCallback(() => {
      fetchData();
      fetchKPIs();
    }, [fetchData, fetchKPIs])
  );

  // Refetch when year changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (goal) {
      navigation.setOptions({ headerShown: false });
    }
  }, [goal, navigation]);

  const onRefresh = () => {
    setRefreshing(true);
    fetchData();
    fetchKPIs();
    fetchInitiatives();
    fetchServices();
    fetchProcesses();
    fetchUnitGoals();
    fetchComments();
  };

  // Comment handlers - integrated with API
  const handleDeleteGoal = () => {
    setShowDeletionImpact(true);
  };

  const confirmDeleteGoal = async () => {
    try {
      setIsDeleting(true);
      const response = await goalsApi.delete(id);
      if ((response as any)?.status === 202) {
        showSuccess(t('deletion.requestSent'));
      } else {
        showSuccess(t('goals.deleteSuccess'));
      }
      navigation.goBack();
    } catch (error: any) {
      if (error.response?.status === 202) {
        showSuccess(t('deletion.requestSent'));
        navigation.goBack();
      } else if (error.response?.status === 409) {
        showError(t('deletion.alreadyPending'));
      } else {
        showError(t('messages.deleteFailed'));
      }
    } finally {
      setIsDeleting(false);
      setShowDeletionImpact(false);
    }
  };

  const handleAddComment = async (text: string) => {
    try {
      // Determine author_type based on user profile
      const authorType = user?.profile?.user_type === 'strategy_team' ? 'strategy_team' : 'org_employee';

      await goalCommentsApi.create({
        strategic_goal: id,
        content: text,
        comment_type: 'note',
        author_type: authorType,
      });
      showSuccess(t('comments.addSuccess'));
      fetchComments(); // Refresh comments
      setShowAddComment(false);
    } catch (err: any) {
      console.error('Error creating comment:', err);
      console.error('Error response:', err.response?.data);
      showError(err.response?.data?.detail || err.response?.data?.content?.[0] || t('comments.addFailed'));
    }
  };

  const handleEditComment = (comment: CommentData) => {
    setEditingComment(comment);
    setShowAddComment(true);
  };

  const handleUpdateComment = async (text: string) => {
    if (editingComment) {
      try {
        await goalCommentsApi.update(editingComment.id, { content: text });
        showSuccess(t('comments.editSuccess'));
        fetchComments(); // Refresh comments
      } catch (err: any) {
        console.error('Error updating comment:', err);
        showError(err.response?.data?.detail || t('comments.editFailed'));
      } finally {
        setEditingComment(null);
        setShowAddComment(false);
      }
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    try {
      const response = await goalCommentsApi.delete(commentId);
      // Check if it was a delete request (org_employee) or direct delete
      showSuccess(t('comments.deleteSuccess'));
      fetchComments(); // Refresh comments
    } catch (err: any) {
      console.error('Error deleting comment:', err);
      console.error('Error response:', err.response?.data);
      // Show specific error message from backend
      const errorMessage = err.response?.data?.detail
        || err.response?.data?.error
        || err.response?.data?.message
        || err.message
        || t('comments.deleteFailed');
      showError(errorMessage);
    }
  };

  const handleCancelComment = () => {
    setShowAddComment(false);
    setEditingComment(null);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (error || !goal) {
    return (
      <EmptyState
        icon="alert-circle-outline"
        title={t('common.error')}
        message={error || t('goals.notFound')}
        actionLabel={t('common.refresh')}
        onAction={fetchData}
      />
    );
  }

  // Shared status thresholds for KPI cards and child rows; null commitment
  // (nothing due yet) renders as on_track — the cards show — with a muted bar.
  const commitmentStatus = (rate: number | null | undefined): 'on_track' | 'at_risk' | 'behind' => {
    if (rate == null) return 'on_track';
    if (rate >= 80) return 'on_track';
    if (rate >= 50) return 'at_risk';
    return 'behind';
  };

  // Transform API KPIs to display format — cards show the KPI's commitment
  // rate (نسبة الالتزام), consistent with the KPIs list page and KPIDetail.
  const kpiCards = strategicKPIs.map((kpi) => {
    const commitment = kpi.commitment_rate ?? null;
    return {
      id: kpi.id,
      title: translate(kpi.name),
      actual: parseFloat(kpi.latest_value || '0'),
      target: parseFloat(kpi.target_value),
      status: commitmentStatus(commitment),
      commitmentRate: commitment,
      unit: isPercentUnit(kpi.unit) ? '%' : undefined,
    };
  });

  // Transform API initiatives to display format
  const initiativeCards = initiatives.map((initiative) => {
    // Commitment rate for the child row bar (null when nothing due yet)
    const percentage = initiative.commitment_rate ?? null;

    const status: 'on_track' | 'at_risk' | 'behind' =
      initiative.status === 'on_hold' || initiative.status === 'cancelled'
        ? 'behind'
        : commitmentStatus(percentage);

    return {
      id: initiative.id,
      title: translate(initiative.title),
      percentage,
      status,
    };
  });

  // Transform services from API to card format
  const servicesData = services.map(service => ({
    id: service.id,
    title: translate(service.name),
    department: (service.owning_units_details || [])
      .map(u => translate(u.name_ar || u.name))
      .join('، ') || t('common.notSpecified'),
    percentage: service.commitment_rate ?? null,
    status: commitmentStatus(service.commitment_rate),
  }));

  // Map processes from API to display format
  const processesData = processes.map((process) => ({
    id: process.id,
    title: translate(process.name),
    department: (process.organizational_units_details || []).map((u: any) => translate(u.name_ar || u.name)).join('، ') || t('common.notSpecified'),
    percentage: process.commitment_rate ?? null,
    status: commitmentStatus(process.commitment_rate),
  }));

  // Transform unit goals from API to display format
  const unitGoalsData = unitGoals.map((unitGoal) => ({
    id: unitGoal.id,
    title: translate(unitGoal.name),
    department: translate(unitGoal.unit_name) || t('common.notSpecified'),
    percentage: unitGoal.commitment_rate ?? null,
    status: commitmentStatus(unitGoal.commitment_rate),
  }));

  // Calculate KPI status distribution from commitment rates. KPIs with a null
  // commitment (nothing due yet) are excluded — they are neither failing nor
  // succeeding, so counting them would skew every bucket.
  const calculateStatusDistribution = () => {
    const rates = strategicKPIs
      .map((kpi: any) => kpi.commitment_rate)
      .filter((rate: any): rate is number => rate != null);

    if (rates.length === 0) {
      return [
        { value: 0, color: '#2CA02C', label: t('status.excellent') },
        { value: 0, color: '#FF7F0E', label: t('status.good') },
        { value: 0, color: '#D62728', label: t('status.needsImprovement') },
      ];
    }

    // Bucket thresholds match the export charts: >=70 ممتاز, >=50 جيد, <50 يحتاج تحسين.
    let excellent = 0;
    let good = 0;
    let needsImprovement = 0;

    rates.forEach((rate: number) => {
      if (rate >= 70) {
        excellent++;
      } else if (rate >= 50) {
        good++;
      } else {
        needsImprovement++;
      }
    });

    const total = rates.length;
    return [
      { value: parseFloat(((excellent / total) * 100).toFixed(2)), color: '#2CA02C', label: t('status.excellent') },
      { value: parseFloat(((good / total) * 100).toFixed(2)), color: '#FF7F0E', label: t('status.good') },
      { value: parseFloat(((needsImprovement / total) * 100).toFixed(2)), color: '#D62728', label: t('status.needsImprovement') },
    ];
  };

  const statusDistribution = calculateStatusDistribution();

  // deviation_percentage is null when no measurement periods are due yet
  const deviationPercentage = goal?.deviation_percentage ?? null;

  return (
    <PageContainer>
      {/* Unified Navbar */}
      <Navbar />

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[
          styles.content,
          { padding: containerPadding }
        ]}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {/* Breadcrumb — context-aware by org-nav source:
            - 'dashboard': came from an org dashboard (/dashboard/:id); the
              trail returns to that org's dashboard.
            - 'strategy' / organizationId: came from the strategy orgs list;
              the trail stays in StrategyStack and returns to the orgs list.
            - otherwise: the global Strategic Goals trail. */}
        <Breadcrumb
          items={(() => {
            if (orgNavSource === 'dashboard' && organizationId) {
              return [
                { label: t('navigation.home') },
                { label: organizationName || t('navigation.dashboard'), route: 'Dashboard', stack: 'DashboardStack', params: { organizationId } },
                { label: t('goals.goalDetails') },
              ];
            }
            if (organizationId) {
              return [
                { label: t('navigation.home') },
                { label: t('strategyDashboard.governmentEntities'), route: 'OrganizationsList', stack: 'StrategyStack' },
                ...(organizationName
                  ? [{ label: organizationName, route: 'OrganizationsList', stack: 'StrategyStack' }]
                  : []),
                { label: t('goals.goalDetails') },
              ];
            }
            return [
              { label: t('navigation.home') },
              { label: t('goals.strategicGoals'), route: 'Goals' },
              { label: t('goals.goalDetails') },
            ];
          })()}
        />

        <PageHeader
          title={translate(goal.title) || ''}
          description={goal.description ? translate(goal.description) : undefined}
          badge={
            goal.status === 'pending' ? (
              <View style={styles.pendingStatusBadge}>
                <Text style={styles.pendingStatusText}>{t('goals.pendingApproval')}</Text>
              </View>
            ) : goal.status === 'draft' ? (
              <View style={styles.draftStatusBadge}>
                <Text style={styles.draftStatusText}>{t('goals.draft')}</Text>
              </View>
            ) : goal.status === 'rejected' ? (
              <View style={styles.rejectedStatusBadge}>
                <Text style={styles.rejectedStatusText}>{t('goals.rejected')}</Text>
              </View>
            ) : goal.status === 'approved' ? (
              <View style={styles.approvedStatusBadge}>
                <Text style={styles.approvedStatusText}>{t('goals.approved')}</Text>
              </View>
            ) : undefined
          }
          actions={
            !isViewOnly ? (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: tokens.spacing.absolute["2"] }}>
                <IconButton
                  variant="destructive"
                  onPress={handleDeleteGoal}
                  icon={<TrashIcon />}
                  accessibilityLabel={t('common.delete')}
                />
                <IconButton
                  variant="secondary"
                  onPress={() => navigation.navigate('AddGoal', { goalId: goal.id })}
                  icon={<SquarePenIcon />}
                  accessibilityLabel={t('common.edit')}
                />
              </View>
            ) : undefined
          }
          contentTrailing={
            <View style={{ flexDirection: isRTL ? 'row-reverse' : 'row', alignItems: 'center', gap: tokens.spacing.absolute["2"], flexWrap: 'wrap' }}>
              <View style={styles.weightRow}>
                <Text style={[styles.weightLabel, { color: themeColors.textMuted }]}>{t('goals.weightFromVision')}</Text>
                <View style={[styles.weightBadge, { borderColor: themeColors.textPrimary }]}>
                  <Text style={[styles.weightValue, { color: themeColors.textPrimary }]}>% {Math.round(Number(goal.weight) || 0)}</Text>
                  <LockIcon size={20} color={themeColors.textPrimary} />
                </View>
              </View>
              {goal.department_name && (
                <View style={styles.organizationBadge}>
                  <Text style={styles.organizationBadgeText}>{translateOrg(goal.department_name)}</Text>
                </View>
              )}
            </View>
          }
        />

        {/* Pending Deletion Banner */}
        {(goal as any).pending_deletion_info && (
          <DeletionApprovalBanner
            deletionInfo={(goal as any).pending_deletion_info}
            canApprove={canApproveDeletion}
            canCancel={isOrgEmployee && (goal as any).pending_deletion_info.requested_by_id === user?.id}
            onApproved={() => { showSuccess(t('deletion.approved')); navigation.goBack(); }}
            onRejected={() => { showSuccess(t('deletion.rejected')); fetchData(); }}
            onCancelled={() => { showSuccess(t('deletion.requestCancelled')); fetchData(); }}
            onViewImpact={() => setShowDeletionImpact(true)}
          />
        )}

        {/* Top Stats Row */}
        <View style={[styles.statsRow, isSmallScreen && styles.statsRowSmall]}>
          {/* Status Distribution - Left Card */}
          <View style={[styles.statCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
            <Text style={[styles.statCardTitle, { color: themeColors.textPrimary }]}>{t('kpis.statusDistribution')}</Text>
            <View style={styles.donutContainer}>
              <View style={styles.donutWrapper}>
                <DonutChart data={statusDistribution} size={100} strokeWidth={24} />
              </View>
              <View style={styles.legendContainer}>
                {statusDistribution.map((item, index) => (
                  <View key={index} style={styles.legendItem}>
                    <Text style={[styles.legendValue, { color: themeColors.textPrimary }]}>{item.value} %</Text>
                    <View style={styles.legendLabelRow}>
                      <Text style={[styles.legendLabel, { color: themeColors.textMuted }]}>{item.label}</Text>
                      <View style={[styles.legendBar, { backgroundColor: item.color }]} />
                    </View>
                  </View>
                ))}
              </View>
            </View>
          </View>

          {/* Deviation Gauge - Middle Card */}
          <View style={[styles.statCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
            <Text style={[styles.statCardTitle, { color: themeColors.textPrimary }]}>{t('goals.deviationPercentage')}</Text>
            {deviationPercentage != null ? (
              <>
                <View style={styles.gaugeContainer}>
                  <CircularGauge
                    percentage={deviationPercentage}
                    size={144}
                    strokeWidth={12}
                    color={deviationPercentage <= 10 ? '#2CA02C' : deviationPercentage <= 25 ? '#FF7F0E' : '#D62728'}
                    backgroundColor={isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.gray["200"]}
                    showDot={true}
                  />
                </View>
                {/* Legend Items */}
                <View style={styles.gaugeLegendItems}>
                  <View style={styles.gaugeLegendRow}>
                    <View style={[styles.gaugeLegendDot, { backgroundColor: '#2CA02C' }]} />
                    <Text style={[styles.gaugeLegendLabel, { color: themeColors.textMuted }]}>{t('deviation.low')}</Text>
                  </View>
                  <View style={styles.gaugeLegendRow}>
                    <View style={[styles.gaugeLegendDot, { backgroundColor: '#FF7F0E' }]} />
                    <Text style={[styles.gaugeLegendLabel, { color: themeColors.textMuted }]}>{t('deviation.medium')}</Text>
                  </View>
                  <View style={styles.gaugeLegendRow}>
                    <View style={[styles.gaugeLegendDot, { backgroundColor: '#D62728' }]} />
                    <Text style={[styles.gaugeLegendLabel, { color: themeColors.textMuted }]}>{t('deviation.high')}</Text>
                  </View>
                </View>
              </>
            ) : (
              <View style={[styles.gaugeContainer, { justifyContent: 'center', alignItems: 'center', minHeight: 144 }]}>
                <Text style={{ fontSize: 36, fontWeight: '700', color: themeColors.textMuted }}>--</Text>
              </View>
            )}
          </View>

          {/* Performance Gauge - Right Card */}
          <View style={[styles.statCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
            <Text style={[styles.statCardTitle, { color: themeColors.textPrimary }]}>{t('kpis.strategicKPIs')}</Text>
            {goal.commitment_rate != null ? (
              <>
                <View style={styles.gaugeContainer}>
                  <CircularGauge
                    percentage={goal.commitment_rate}
                    size={144}
                    strokeWidth={12}
                    color={commitmentColor(goal.commitment_rate)}
                    backgroundColor={isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.gray["200"]}
                    showDot={true}
                  />
                </View>
                {/* Legend Items */}
                <View style={styles.gaugeLegendItems}>
                  <View style={styles.gaugeLegendRow}>
                    <View style={[styles.gaugeLegendDot, { backgroundColor: '#2CA02C' }]} />
                    <Text style={[styles.gaugeLegendLabel, { color: themeColors.textMuted }]}>{t('performance.excellent')}</Text>
                  </View>
                  <View style={styles.gaugeLegendRow}>
                    <View style={[styles.gaugeLegendDot, { backgroundColor: '#FF7F0E' }]} />
                    <Text style={[styles.gaugeLegendLabel, { color: themeColors.textMuted }]}>{t('performance.medium')}</Text>
                  </View>
                  <View style={styles.gaugeLegendRow}>
                    <View style={[styles.gaugeLegendDot, { backgroundColor: '#D62728' }]} />
                    <Text style={[styles.gaugeLegendLabel, { color: themeColors.textMuted }]}>{t('performance.poor')}</Text>
                  </View>
                </View>
              </>
            ) : (
              <View style={[styles.gaugeContainer, { justifyContent: 'center', alignItems: 'center', minHeight: 144 }]}>
                <Text style={{ fontSize: 36, fontWeight: '700', color: themeColors.textMuted }}>--</Text>
              </View>
            )}
          </View>
        </View>

        {/* Main Content Area */}
        <View style={[styles.mainContentRow, isSmallScreen && styles.mainContentRowSmall]}>
          {/* Left Column - Notes */}
          <View style={[styles.notesColumn, isSmallScreen && styles.notesColumnSmall]}>
            <View style={[styles.notesCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
              {/* Header row with title and add button */}
              <View style={styles.notesHeader}>
                {/* Show add comment button for users who can comment (org_employee, org_executive, strategy_team, superuser) */}
                {canComment && (
                  <TouchableOpacity
                    style={styles.notesAddButton}
                    onPress={() => setShowAddComment(true)}
                  >
                    <MessageCirclePlusIcon size={15} color={themeColors.iconDefault} />
                  </TouchableOpacity>
                )}
                <View style={styles.notesTitleContainer}>
                  <Text style={[styles.notesTitle, { color: themeColors.textPrimary }]}>{t('comments.notesAndComments')}</Text>
                </View>
              </View>
              {/* Comments list */}
              <View style={styles.commentsList}>
                {comments.map((comment) => (
                  <CommentCard
                    key={comment.id}
                    comment={comment}
                    onEdit={handleEditComment}
                    onDelete={handleDeleteComment}
                  />
                ))}

                {/* Add Comment Input */}
                {showAddComment && (
                  <AddCommentInput
                    onSubmit={editingComment ? handleUpdateComment : handleAddComment}
                    onCancel={handleCancelComment}
                    editingComment={editingComment}
                  />
                )}
              </View>
            </View>
          </View>

          {/* Right Column - Links */}
          <View style={[styles.linksColumn, isSmallScreen && styles.linksColumnSmall]}>
            <View style={[styles.linksCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
              <Text style={[styles.linksCardTitle, { color: themeColors.textPrimary }]}>{t('goals.goalLinks')}</Text>

              {/* Strategic KPIs Section - with muted background per Figma */}
              <View style={[styles.strategicKpiSection, { backgroundColor: themeColors.backgroundAlt }]}>
                {/* Section header row */}
                <View style={styles.strategicKpiHeader}>
                  {/* Add KPI Button - Hidden for strategy_team and view-only users */}
                  {/* Hide add button for strategy_team and view-only users */}
                  {!isStrategyTeam && !isViewOnly && (
                    <TouchableOpacity
                      style={styles.strategicKpiAddButton}
                      onPress={() => navigation.navigate('AddGoal', { goalId: id, scrollToKpi: true })}
                    >
                      <Text style={styles.strategicKpiAddButtonText}>{t('kpis.addKPI')}</Text>
                      <PlusIcon size={13} color={tokens.colors.semantic.general.foreground} />
                    </TouchableOpacity>
                  )}
                  <View style={styles.strategicKpiHeaderTitleContainer}>
                    <Text style={[styles.strategicKpiHeaderTitle, { color: themeColors.textPrimary }]}>{t('kpis.strategicKPIs')}</Text>
                  </View>
                </View>
                {/* KPI cards row - horizontally scrollable with RTL transform */}
                <View style={styles.rtlWrapper}>
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={true}
                    contentContainerStyle={styles.strategicKpiCardsRow}
                    style={styles.horizontalScrollRTL}
                  >
                    {kpiCards.map((kpi) => (
                      <View key={kpi.id} style={[styles.rtlCard, { width: horizontalCardWidth }]}>
                        <TargetAchievementCard
                          title={kpi.title}
                          achieved={kpi.actual}
                          target={kpi.target}
                          unit={kpi.unit || 'عدد'}
                          achievedColor={
                            kpi.status === 'on_track' ? '#2ca02c' :
                            kpi.status === 'at_risk' ? tokens.colors.raw.yellow["500"] :
                            tokens.colors.raw.red["500"]
                          }
                          percentage={kpi.commitmentRate}
                          onViewDetails={() => navigation.navigate('KPIDetail', { id: kpi.id, organizationId, organizationName, orgNavSource })}
                        />
                      </View>
                    ))}
                  </ScrollView>
                </View>
              </View>

              {/* Initiatives Section - المبادرات */}
              <View style={[styles.linkageSectionWrapper, { backgroundColor: themeColors.backgroundAlt }]}>
                <View style={styles.linkageSectionHeader}>
                  {/* Add Initiative Button - Hidden for strategy_team and view-only users */}
                  {/* Hide add button for strategy_team and view-only users */}
                  {!isStrategyTeam && !isViewOnly && (
                    <TouchableOpacity
                      style={styles.linkageSectionAddButton}
                      onPress={() => navigation.navigate('AddInitiative', { goalId: id })}
                    >
                      <Text style={styles.linkageSectionAddButtonText}>{t('initiatives.addInitiative')}</Text>
                      <PlusIcon size={13} color={themeColors.iconDefault} />
                    </TouchableOpacity>
                  )}
                  <View style={styles.linkageSectionHeaderTitleContainer}>
                    <Text style={[styles.linkageSectionHeaderTitle, { color: themeColors.textPrimary }]}>{t('dashboard.initiatives')}</Text>
                  </View>
                </View>
                <View style={styles.rtlWrapper}>
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={true}
                    contentContainerStyle={styles.linkageCardsRow}
                    style={styles.horizontalScrollRTL}
                  >
                    {initiativeCards.map((item) => (
                      <View key={item.id} style={styles.rtlCard}>
                        <LinkageCard
                          title={item.title}
                          percentage={item.percentage}
                          status={item.status}
                          cardWidth={horizontalCardWidth}
                          onPress={() => navigation.navigate('InitiativeDetail', { id: item.id })}
                        />
                      </View>
                    ))}
                    {initiativeCards.length === 0 && !initiativesLoading && (
                      <View style={styles.rtlCard}>
                        <Text style={styles.emptyText}>{t('initiatives.noInitiatives')}</Text>
                      </View>
                    )}
                  </ScrollView>
                </View>
              </View>

              {/* Services Section - الخدمات */}
              <View style={[styles.linkageSectionWrapper, { backgroundColor: themeColors.backgroundAlt }]}>
                <View style={styles.linkageSectionHeader}>
                  {/* Hide add button for strategy_team and view-only users */}
                  {!isStrategyTeam && !isViewOnly && (
                    <TouchableOpacity
                      style={styles.linkageSectionAddButton}
                      onPress={() => navigation.navigate('AddService', { goalId: id })}
                    >
                      <Text style={styles.linkageSectionAddButtonText}>{t('services.addService')}</Text>
                      <PlusIcon size={13} color={themeColors.iconDefault} />
                    </TouchableOpacity>
                  )}
                  <View style={styles.linkageSectionHeaderTitleContainer}>
                    <Text style={[styles.linkageSectionHeaderTitle, { color: themeColors.textPrimary }]}>{t('navigation.services')}</Text>
                  </View>
                </View>
                <View style={styles.rtlWrapper}>
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={true}
                    contentContainerStyle={styles.linkageCardsRow}
                    style={styles.horizontalScrollRTL}
                  >
                    {servicesLoading ? (
                      <View style={styles.rtlCard}>
                        <ActivityIndicator size="small" color={COLORS.primary} style={{ padding: tokens.spacing.absolute["5"] }} />
                      </View>
                    ) : servicesData.length > 0 ? (
                      servicesData.map((item) => (
                        <View key={item.id} style={styles.rtlCard}>
                          <LinkageCard
                            title={item.title}
                            department={item.department}
                            percentage={item.percentage}
                            status={item.status}
                            cardWidth={horizontalCardWidth}
                            onPress={() => navigation.navigate('ServiceDetail', { id: item.id })}
                          />
                        </View>
                      ))
                    ) : (
                      <View style={styles.rtlCard}>
                        <Text style={styles.emptyText}>{t('services.noServices')}</Text>
                      </View>
                    )}
                  </ScrollView>
                </View>
              </View>

              {/* Processes Section - العمليات الرئيسية */}
              <View style={[styles.linkageSectionWrapper, { backgroundColor: themeColors.backgroundAlt }]}>
                <View style={styles.linkageSectionHeader}>
                  {/* Hide add button for strategy_team and view-only users */}
                  {!isStrategyTeam && !isViewOnly && (
                    <TouchableOpacity
                      style={styles.linkageSectionAddButton}
                      onPress={() => navigation.navigate('AddProcess', { goalId: id })}
                    >
                      <Text style={styles.linkageSectionAddButtonText}>{t('processes.addProcess')}</Text>
                      <PlusIcon size={13} color={themeColors.iconDefault} />
                    </TouchableOpacity>
                  )}
                  <View style={styles.linkageSectionHeaderTitleContainer}>
                    <Text style={[styles.linkageSectionHeaderTitle, { color: themeColors.textPrimary }]}>{t('processes.mainProcesses')}</Text>
                  </View>
                </View>
                <View style={styles.rtlWrapper}>
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={true}
                    contentContainerStyle={styles.linkageCardsRow}
                    style={styles.horizontalScrollRTL}
                  >
                    {processesLoading ? (
                      <View style={styles.rtlCard}>
                        <ActivityIndicator size="small" color={COLORS.primary} style={{ padding: tokens.spacing.absolute["5"] }} />
                      </View>
                    ) : processesData.length > 0 ? (
                      processesData.map((item) => (
                        <View key={item.id} style={styles.rtlCard}>
                          <LinkageCard
                            title={item.title}
                            department={item.department}
                            percentage={item.percentage}
                            status={item.status}
                            cardWidth={horizontalCardWidth}
                            onPress={() => navigation.navigate('ProcessDetail', { id: item.id })}
                          />
                        </View>
                      ))
                    ) : (
                      <View style={styles.rtlCard}>
                        <Text style={styles.emptyText}>{t('processes.noProcesses')}</Text>
                      </View>
                    )}
                  </ScrollView>
                </View>
              </View>

              {/* Organizational Unit Goals Section - أهداف الوحدات التنظيمية */}
              <View style={[styles.linkageSectionWrapper, { backgroundColor: themeColors.backgroundAlt }]}>
                <View style={styles.linkageSectionHeader}>
                  {/* Hide add button for strategy_team and view-only users */}
                  {!isStrategyTeam && !isViewOnly && (
                    <TouchableOpacity
                      style={styles.linkageSectionAddButton}
                      onPress={() => navigation.navigate('AddUnitGoal', { strategicGoalId: id })}
                    >
                      <Text style={styles.linkageSectionAddButtonText}>{t('unitGoals.addUnitGoal')}</Text>
                      <PlusIcon size={13} color={themeColors.iconDefault} />
                    </TouchableOpacity>
                  )}
                  <View style={styles.linkageSectionHeaderTitleContainer}>
                    <Text style={[styles.linkageSectionHeaderTitle, { color: themeColors.textPrimary }]}>{t('unitGoals.title')}</Text>
                  </View>
                </View>
                <View style={styles.rtlWrapper}>
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={true}
                    contentContainerStyle={styles.linkageCardsRow}
                    style={styles.horizontalScrollRTL}
                  >
                    {unitGoalsLoading ? (
                      <View style={styles.rtlCard}>
                        <ActivityIndicator size="small" color={COLORS.primary} style={{ padding: tokens.spacing.absolute["5"] }} />
                      </View>
                    ) : unitGoalsData.length > 0 ? (
                      unitGoalsData.map((item) => (
                        <View key={item.id} style={styles.rtlCard}>
                          <LinkageCard
                            title={item.title}
                            department={item.department}
                            percentage={item.percentage}
                            status={item.status}
                            cardWidth={horizontalCardWidth}
                            onPress={() => navigation.navigate('UnitGoalDetail', { id: item.id })}
                          />
                        </View>
                      ))
                    ) : (
                      <View style={styles.rtlCard}>
                        <Text style={styles.emptyText}>{t('unitGoals.noUnitGoals')}</Text>
                      </View>
                    )}
                  </ScrollView>
                </View>
              </View>
            </View>
          </View>
        </View>
        <EvidenceTable
          relatedType="strategic_goal"
          filterRelatedId={id}
          title={t('evidence.sectionTitle')}
          canManage={canManageEvidence}
          showOrgColumn={canViewAllEvidence}
          onAddPress={() => navigation.navigate('AddEvidence', {
            relatedType: 'strategic_goal', relatedId: id, contextLabel: t('goals.strategicGoals'),
          })}
          onEditPress={(ev) => navigation.navigate('AddEvidence', {
            evidenceId: ev.id, relatedType: ev.related_type, relatedId: ev.related_id, contextLabel: t('goals.strategicGoals'),
          })}
        />
      </ScrollView>

      <DeletionImpactModal
        visible={showDeletionImpact}
        onClose={() => setShowDeletionImpact(false)}
        onConfirm={confirmDeleteGoal}
        entityName={goal?.title || ''}
        fetchImpact={async () => {
          const response = await goalsApi.deletionImpact(id);
          return response.data;
        }}
        isOrgEmployee={isOrgEmployee}
        isSubmitting={isDeleting}
      />

    </PageContainer>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    // Padding set dynamically via inline style for responsiveness
  },
  // Breadcrumb
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
    marginBottom: tokens.spacing.absolute["6"],
  },
  breadcrumbSmall: {
    flexWrap: 'wrap',
    gap: tokens.spacing.absolute["1"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  breadcrumbLink: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },
  breadcrumbActive: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general["Main Color"],
  },
  // Pending Status Badge
  pendingStatusBadge: {
    backgroundColor: '#9467BD',
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["1_5"],
    borderRadius: 40,
    alignSelf: 'flex-end',
  },
  pendingStatusText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.white,
  },
  draftStatusBadge: {
    backgroundColor: tokens.colors.raw.gray["500"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["1_5"],
    borderRadius: 40,
    alignSelf: 'flex-end',
  },
  draftStatusText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.white,
  },
  rejectedStatusBadge: {
    backgroundColor: tokens.colors.raw.red["500"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["1_5"],
    borderRadius: 40,
    alignSelf: 'flex-end',
  },
  rejectedStatusText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.white,
  },
  approvedStatusBadge: {
    backgroundColor: tokens.colors.raw.green["500"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["1_5"],
    borderRadius: 40,
    alignSelf: 'flex-end',
  },
  approvedStatusText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.white,
  },
  // Organization Badge
  organizationBadge: {
    backgroundColor: tokens.colors.raw.amber["100"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["1_5"],
    borderRadius: tokens.radii.absolute["radius-4"],
    alignSelf: 'flex-end',
  },
  organizationBadgeText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: '#92400E',
  },
  weightRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2_5"],
  },
  weightBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.semantic.general.foreground,
    borderRadius: 40,
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["1"],
    gap: tokens.spacing.absolute["2"],
  },
  weightLabel: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"],
  },
  weightValue: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: tokens.colors.semantic.general.foreground,
  },
  // Stats Row
  statsRow: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["5"],
    marginBottom: tokens.spacing.absolute["6"],
  },
  statsRowSmall: {
    flexDirection: 'column',
    gap: tokens.spacing.absolute["4"],
  },
  statCard: {
    flex: 1,
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["5"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    overflow: 'hidden',
  },
  statCardTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["5"],
  },
  gaugeContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
  },
  gaugeLegendItems: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: tokens.spacing.absolute["3"],
    marginTop: tokens.spacing.absolute["3"],
  },
  gaugeLegendRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1_5"],
  },
  gaugeLegendDot: {
    width: 10,
    height: 10,
    borderRadius: tokens.radii.absolute["radius-5"],
  },
  gaugeLegendLabel: {
    fontSize: tokens.typography.paragraph["paragraph caption font size"],
    fontFamily: FONTS.regular,
  },
  donutContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 40,
  },
  donutWrapper: {
    marginEnd: tokens.spacing.absolute["5"],
  },
  legendContainer: {
    flex: 1,
    gap: tokens.spacing.absolute["6"],
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
  },
  legendLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1"],
    flex: 1,
    justifyContent: 'flex-end',
  },
  legendBar: {
    width: 8,
    height: 32,
    borderRadius: tokens.radii.absolute["radius-2"],
  },
  legendLabel: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"],
  },
  legendValue: {
    fontSize: tokens.typography["heading 4"]["font size"],
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: '#303030',
  },
  // Main Content Row
  mainContentRow: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["6"],
  },
  mainContentRowSmall: {
    flexDirection: 'column-reverse',
    gap: tokens.spacing.absolute["4"],
  },
  notesColumn: {
    width: 280,
  },
  notesColumnSmall: {
    width: '100%',
  },
  linksColumn: {
    flex: 1,
  },
  linksColumnSmall: {
    flex: 0,
  },
  // Notes Card (matches Figma node 2462:154109)
  notesCard: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    overflow: 'visible',
  },
  notesHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: tokens.spacing.absolute["6"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  notesAddButton: {
    width: 44,
    height: 44,
    borderRadius: tokens.radii.absolute["radius-8"],
    backgroundColor: 'rgba(51, 65, 85, 0.05)',
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    justifyContent: 'center',
    alignItems: 'center',
  },
  notesTitleContainer: {
    flex: 1,
    alignItems: 'flex-end',
  },
  notesTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  commentsList: {
    gap: tokens.spacing.absolute["2"],
    overflow: 'visible',
  },
  // Comment Card styles
  commentCard: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-4"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["1"],
    position: 'relative',
    zIndex: 1,
  },
  commentCardMenuOpen: {
    zIndex: 1000,
  },
  commentHeaderRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["0_5"],
  },
  commentMenuButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  commentNameDateContainer: {
    flex: 1,
    alignItems: 'flex-end',
  },
  commentAvatar: {
    width: 24,
    height: 24,
    borderRadius: tokens.radii.absolute["radius-12"],
    justifyContent: 'center',
    alignItems: 'center',
  },
  commentAvatarText: {
    fontSize: 10,
    fontFamily: FONTS.regular,
    color: '#FCFCFC',
  },
  commentName: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  commentDate: {
    fontSize: 10,
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"],
    textAlign: getTextAlign(isRTL),
  },
  commentSeparator: {
    height: 1,
    backgroundColor: tokens.colors.raw.slate["200"],
    marginVertical: tokens.spacing.absolute["0"],
  },
  commentTextContainer: {
    paddingVertical: tokens.spacing.absolute["0"],
  },
  commentText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 20,
  },
  // Comment Menu styles
  commentMenuWrapper: {
    position: 'relative',
    zIndex: 100,
  },
  commentMenuDropdown: {
    position: 'absolute',
    top: 28,
    start: -8,
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.2)',
    elevation: 16,
    minWidth: 160,
    zIndex: 1000,
    paddingVertical: tokens.spacing.absolute["3"],
    paddingHorizontal: tokens.spacing.absolute["1"],
  },
  commentMenuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2_5"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["3"],
    minHeight: 44,
  },
  commentMenuItemText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },
  commentMenuItemTextDelete: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.red["500"],
  },
  commentMenuSeparator: {
    height: 1,
    backgroundColor: tokens.colors.raw.slate["200"],
    marginHorizontal: tokens.spacing.absolute["2"],
  },
  // Add Comment Input styles (matches Figma node 2569:211200)
  addCommentCard: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-4"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["1"],
  },
  addCommentHeader: {
    paddingVertical: tokens.spacing.absolute["0_5"],
    alignItems: 'flex-end',
  },
  addCommentHeaderText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  addCommentSeparator: {
    height: 1,
    backgroundColor: tokens.colors.raw.slate["200"],
  },
  addCommentInputContainer: {
    minHeight: 40,
    paddingVertical: tokens.spacing.absolute["1"],
  },
  addCommentInput: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    minHeight: 32,
    padding: tokens.spacing.absolute["0"],
  },
  addCommentActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["0_5"],
  },
  addCommentActionButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  // Links Card
  linksCard: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    padding: tokens.spacing.absolute["5"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: '#E5E5E5',
  },
  linksCardTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.medium,
    color: '#171717',
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["5"],
  },
  // Strategic KPI Section (matches Figma node 2462:154310)
  strategicKpiSection: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    gap: tokens.spacing.absolute["2_5"],
    marginBottom: tokens.spacing.absolute["6"],
    overflow: 'visible',
  },
  strategicKpiHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacing.absolute["5"],
  },
  strategicKpiHeaderTitleContainer: {
    flex: 1,
    alignItems: 'flex-end',
  },
  strategicKpiHeaderTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  strategicKpiAddButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing.absolute["1_5"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    minHeight: 44,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["300"],
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  strategicKpiAddButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },
  horizontalScrollRTL: {
    flexGrow: 0,
  },
  // RTL wrapper - flips the entire ScrollView horizontally so cards start from right
  rtlWrapper: {
    transform: [{ scaleX: -1 }],
  },
  // RTL card - flips each card back to normal orientation
  rtlCard: {
    transform: [{ scaleX: -1 }],
  },
  strategicKpiCardsRow: {
    flexDirection: 'row' as const,
    gap: tokens.spacing.absolute["6"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingBottom: tokens.spacing.absolute["3"],
  },
  // Strategic KPI Card styles
  // Linkage Section Wrapper (for المبادرات, الخدمات, العمليات, أهداف الوحدات)
  linkageSectionWrapper: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    gap: tokens.spacing.absolute["2_5"],
    marginBottom: tokens.spacing.absolute["2_5"],
    overflow: 'visible',
  },
  linkageSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacing.absolute["5"],
  },
  linkageSectionHeaderTitleContainer: {
    flex: 1,
    alignItems: 'flex-end',
  },
  linkageSectionHeaderTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  linkageSectionAddButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing.absolute["1_5"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    minHeight: 44,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["300"],
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  linkageSectionAddButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },
  linkageCardsRow: {
    flexDirection: 'row' as const,
    gap: tokens.spacing.absolute["6"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingBottom: tokens.spacing.absolute["3"],
  },
  emptyLinkageCard: {
    backgroundColor: tokens.colors.raw.slate["50"],
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderStyle: 'dashed',
    padding: tokens.spacing.absolute["6"],
    minWidth: 200,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyLinkageText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: '#94A3B8',
    textAlign: 'center' as const,
  },
  emptyText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"],
    textAlign: 'center' as const,
    padding: tokens.spacing.absolute["5"],
  },
  // Linkage Card styles
  linkageCard: {
    width: 280,
    flexShrink: 0, // Prevent card from shrinking
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    padding: tokens.spacing.absolute["3"],
    gap: tokens.spacing.absolute["4"],
    overflow: 'hidden',
  },
  linkageCardTitleContainer: {
    alignItems: 'flex-end',
  },
  linkageCardTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 20,
  },
  linkageCardDepartmentContainer: {
    gap: tokens.spacing.absolute["2"],
  },
  linkageCardDepartmentBadge: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-4"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["0_5"],
    width: '100%',
  },
  linkageCardDepartmentText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  linkageCardProgressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
  },
  linkageCardValueBadge: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: 100,
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["0_5"],
  },
  linkageCardValueText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: 'center',
  },
  linkageCardProgressContainer: {
    flex: 1,
  },
  linkageCardProgressBg: {
    height: 8,
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    position: 'relative',
    overflow: 'visible',
  },
  linkageCardProgressFill: {
    position: 'absolute',
    end: 0,
    top: 0,
    bottom: 0,
    borderRadius: tokens.radii.absolute["radius-8"],
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    overflow: 'visible',
  },
  linkageCardProgressDot: {
    width: 14,
    height: 14,
    borderRadius: tokens.radii.absolute["radius-7"],
    borderWidth: 2,
    borderColor: '#FCFCFC',
    position: 'absolute',
    start: -7,
    top: -3,
    zIndex: 2,
  },
  linkageCardDetailsLink: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: tokens.spacing.absolute["2"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    minHeight: 44,
    borderRadius: tokens.radii.absolute["radius-4"],
  },
  linkageCardDetailsText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  // Comment Name Row (contains name and edited tag)
  commentNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1_5"],
  },
  // Badge row for user type and edited tag (below name)
  commentBadgeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["1_5"],
    marginTop: tokens.spacing.absolute["0_5"],
  },
  // User Type Badge
  userTypeBadge: {
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["0_5"],
    borderRadius: tokens.radii.absolute["radius-12"],
  },
  userTypeBadgeText: {
    fontSize: 10,
    fontFamily: FONTS.regular,
  },
  // Edited Tag styles
  editedTag: {
    backgroundColor: tokens.colors.raw.amber["100"],
    borderRadius: tokens.radii.absolute["radius-4"],
    paddingHorizontal: tokens.spacing.absolute["1_5"],
    paddingVertical: tokens.spacing.absolute["0_5"],
  },
  editedTagText: {
    fontSize: 10,
    fontFamily: FONTS.regular,
    color: '#92400E',
  },
  // Original Text Modal styles
  originalTextModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  originalTextModalContent: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    width: '80%',
    maxWidth: 400,
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.25)',
    elevation: 10,
  },
  originalTextModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["3"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.slate["200"],
  },
  originalTextModalTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  originalTextModalBody: {
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["4"],
  },
  originalTextModalText: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 22,
  },
});

const styles = getStyles(true);
